//
// # EditRouteWidthDialog
//
// Command-palette style dialog for setting a route width in a layout. Used both
// while drawing a route (sets the pending draw width) and for an existing route
// (edits that route). Validates against the layer's [min, max] bounds; the value
// is snapped to the manufacturing grid and collapsed to the layer default on
// submit via the shared normalizeWidthToConstraints helper.
//

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { layout } from "GenAlphaModel";

import { useWorkspace, useWorkspaceSnapshot } from "../context";

export interface EditRouteWidthDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function EditRouteWidthDialog({
  open,
  onClose,
}: EditRouteWidthDialogProps) {
  const workspace = useWorkspace();
  const { editRouteWidthDialog } = useWorkspaceSnapshot();

  const [widthText, setWidthText] = useState("");

  // Prefill with the current effective width whenever the dialog (re)opens.
  useEffect(() => {
    if (open && editRouteWidthDialog) {
      setWidthText(String(editRouteWidthDialog.currentWidth));
    }
  }, [open, editRouteWidthDialog?.currentWidth]);

  // Validate the typed value against the bounds. Returns an error string, or
  // null when the value is an acceptable whole number within [min, max].
  const error = useMemo<string | null>(() => {
    if (!editRouteWidthDialog) return null;
    const { minWidth, maxWidth } = editRouteWidthDialog;
    const trimmed = widthText.trim();
    if (trimmed === "") return "Enter a width in nm";
    const n = Number(trimmed);
    if (!Number.isFinite(n) || !Number.isInteger(n)) {
      return "Enter a whole number of nm";
    }
    if (n < minWidth) return `Min width: ${minWidth}nm`;
    if (n > maxWidth) return `Max width: ${maxWidth}nm`;
    return null;
  }, [editRouteWidthDialog, widthText]);

  const handleSubmit = useCallback(() => {
    if (!editRouteWidthDialog || error) return;
    const {
      designIdent,
      minWidth,
      maxWidth,
      defaultWidth,
      manufacturingGridNm,
    } = editRouteWidthDialog;
    // Snap/clamp and collapse-to-default through the single shared normalizer.
    const width = layout.normalizeWidthToConstraints(
      Number(widthText.trim()),
      { min: minWidth, max: maxWidth, default: defaultWidth },
      manufacturingGridNm
    );
    workspace.updateRouteWidth(designIdent, width);
    onClose();
  }, [editRouteWidthDialog, error, widthText, workspace, onClose]);

  const handleUseDefault = useCallback(() => {
    if (!editRouteWidthDialog) return;
    workspace.updateRouteWidth(editRouteWidthDialog.designIdent, null);
    onClose();
  }, [editRouteWidthDialog, workspace, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    },
    [handleSubmit, onClose]
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  if (!open || !editRouteWidthDialog) return null;

  const { minWidth, maxWidth, defaultWidth, routeId } = editRouteWidthDialog;
  const title = routeId !== undefined ? "Set Segment Width" : "Set Draw Width";

  return createPortal(
    <div
      className="command-palette-backdrop"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div className="command-palette add-instance-dialog" role="dialog">
        <div className="command-palette-prompt-label">{title}</div>
        <input
          className={
            error
              ? "command-palette-input add-instance-param-input-error"
              : "command-palette-input"
          }
          type="number"
          placeholder={`Width in nm (default: ${defaultWidth})`}
          value={widthText}
          onChange={(e) => {
            setWidthText(e.target.value);
          }}
          autoFocus
        />
        {error ? (
          <div className="add-instance-param-error">{error}</div>
        ) : (
          <div className="add-instance-param-hint">
            Min: {minWidth}nm · Max: {maxWidth}nm · Default: {defaultWidth}nm
          </div>
        )}
        <div className="add-instance-footer">
          <span className="add-instance-hint">
            Enter to apply · Escape to cancel
          </span>
          <button className="add-instance-btn" onClick={handleUseDefault}>
            Use Default
          </button>
          <button
            className="add-instance-btn"
            onClick={handleSubmit}
            disabled={error !== null}
          >
            Apply
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
